import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTaskStore } from '../../store/useTaskStore';
import { useObjectStore } from '../../store/useObjectStore';
import { useServiceStore } from '../../store/useServiceStore';
import { Card } from '../../components/Shared/Card';
import { Loading } from '../../components/Shared/Loading';
import { Badge } from '../../components/Shared/Badge';
import { Button } from '../../components/Shared/Button';
import { SlideOver } from '../../components/Shared/SlideOver';
import { EmptyState } from '../../components/Shared/EmptyState';
import { Pagination } from '../../components/Shared/Pagination';
import { ConfirmDialog } from '../../components/Shared/ConfirmDialog';
import SearchSelect from '../../components/Shared/SearchSelect';
import { usePagedList } from '../../hooks/usePagedList';
import { useUrlListState } from '../../hooks/useUrlListState';
import { useToastStore } from '../../store/useToastStore';
import { showApiError } from '../../errors/showApiError';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
} from '../../components/Shared/Table';
import { Plus, Search, Edit2, Trash2, Package, Clock, Eye, Download, Upload } from 'lucide-react';
import { useTranslation } from '../../i18n';
import { ExcelImportModal } from '../../components/Shared/ExcelImportModal';
import { useExcelIo } from '../../hooks/useExcelIo';
import { TaskForm } from './components/TaskForm';
import type { TaskListDto } from '../../types';

export default function TasksPage() {
  const { t } = useTranslation(['tasks', 'common']);
  const navigate = useNavigate();
  const { tasks, fullListLoaded, loading, error, fetchTasks, deleteTask } = useTaskStore();
  const { objects, fetchAllObjects } = useObjectStore();
  const { services, fetchServices } = useServiceStore();
  const { addToast } = useToastStore();
  
  const [listState, setListState] = useUrlListState(
    { q: '', status: 'all', object: '', service: '', page: 1 },
    { status: ['all', 'active', 'inactive'] },
  );
  const {
    q: searchTerm,
    status: filterStatus,
    object: filterObject,
    service: filterService,
  } = listState;
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const { exporting, exportExcel } = useExcelIo('tasks');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<TaskListDto | null>(null);

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<{ id: string; name: string } | null>(null);

  useEffect(() => {
    Promise.all([
      fetchTasks().then(() => setInitialLoadComplete(true)),
      fetchAllObjects(), // Load all objects for dropdown
      fetchServices(),
    ]);
  }, [fetchTasks, fetchAllObjects, fetchServices]);

  const filteredTasks = tasks.filter((task) => {
    if (searchTerm) {
      const matchesSearch = 
        task.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.serviceName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.objectName?.toLowerCase().includes(searchTerm.toLowerCase());
      if (!matchesSearch) return false;
    }

    if (filterStatus === 'active' && !task.isActive) return false;
    if (filterStatus === 'inactive' && task.isActive) return false;
    
    if (filterObject && task.objectId !== filterObject) return false;
    if (filterService && task.serviceId !== filterService) return false;

    return true;
  });

  const { page, pageCount, pageItems, goToPage } = usePagedList(filteredTasks, undefined, [
    listState.page,
    (nextPage) => setListState({ page: nextPage }),
  ]);

  // Export follows the filter, so the file mirrors what the list shows. The schedule-rule
  // sheet follows along server-side — rules whose task is absent would be unreadable.
  const exportIds =
    filteredTasks.length === tasks.length
      ? undefined
      : filteredTasks.map((task) => task.taskId).filter((id): id is string => !!id);

  const openDeleteDialog = (task: TaskListDto) => {
    if (!task.taskId || !task.name) return;
    setTaskToDelete({ id: task.taskId, name: task.name });
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!taskToDelete) return;

    try {
      const deactivated = await deleteTask(taskToDelete.id);
      addToast(t(deactivated ? 'tasks:messages.deactivated' : 'tasks:messages.deleted'), 'success');
    } catch (err) {
      showApiError(err);
    } finally {
      setIsDeleteDialogOpen(false);
      setTaskToDelete(null);
    }
  };

  const handleEdit = (task: TaskListDto) => {
    setSelectedTask(task);
    setIsEditModalOpen(true);
  };

  const handleCloseModals = () => {
    setIsCreateModalOpen(false);
    setIsEditModalOpen(false);
    setSelectedTask(null);
  };

  const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins} Min`;
    if (mins === 0) return `${hours} Std`;
    return `${hours} Std ${mins} Min`;
  };

  // A full list from an earlier visit shows right away and refreshes in place.
  if (!initialLoadComplete && !fullListLoaded) {
    return <Loading />;
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header card */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-[22px] sm:text-[24px] font-semibold tracking-tight text-slate-900">
              {t('tasks:title')}
            </h1>
            <p className="text-[13px] text-slate-500 mt-0.5">
              {t('tasks:subtitle')}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 shrink-0">
            <Button
              variant="secondary"
              onClick={() => exportExcel(exportIds)}
              loading={exporting}
              leftIcon={<Download className="w-4 h-4" strokeWidth={2} />}
              className="w-full sm:w-auto"
            >
              {exporting
                ? t('common:excelImport.exporting')
                : exportIds
                  ? t('common:excelImport.exportFiltered', { total: exportIds.length })
                  : t('tasks:actions.exportExcel')}
            </Button>
            <Button
              variant="secondary"
              onClick={() => setIsImportOpen(true)}
              disabled={exporting}
              leftIcon={<Upload className="w-4 h-4" strokeWidth={2} />}
              className="w-full sm:w-auto"
            >
              {t('tasks:actions.importExcel')}
            </Button>
            <Button
              onClick={() => setIsCreateModalOpen(true)}
              leftIcon={<Plus className="w-4 h-4" strokeWidth={2} />}
              className="w-full sm:w-auto"
            >
              <span className="hidden sm:inline">{t('tasks:newTask')}</span>
              <span className="sm:hidden">{t('common:actions.create')}</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="rounded-xl border border-slate-200 bg-white px-3 sm:px-4 py-2.5">
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:items-center">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" strokeWidth={2} />
              <input
                type="text"
                placeholder={t('tasks:placeholders.search')}
                value={searchTerm}
                onChange={(e) => setListState({ q: e.target.value })}
                className="w-full pl-9 pr-3 py-1.5 rounded-md bg-white border border-slate-200 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#00A871]/30 focus:border-[#00A871]/50"
              />
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <select
              value={filterStatus}
              onChange={(e) => setListState({ status: e.target.value })}
              aria-label={t('tasks:filters.statusLabel')}
              className="px-3 py-1.5 rounded-md bg-white border border-slate-200 text-[13px] text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#00A871]/30 focus:border-[#00A871]/50"
            >
              <option value="all">{t('common:filters.allStatus')}</option>
              <option value="active">{t('common:status.active')}</option>
              <option value="inactive">{t('common:status.inactive')}</option>
            </select>
            <SearchSelect
              options={objects
                .map((obj) => ({ id: obj.id ?? '', label: obj.name ?? '' }))
                .filter((o) => o.id)}
              value={filterObject}
              onChange={(value) => setListState({ object: value })}
              placeholder={t('tasks:filters.allObjects')}
              noResultsText={t('tasks:filters.noResults')}
              className="w-full sm:w-52"
            />
            <SearchSelect
              options={services
                .map((svc) => ({ id: svc.id ?? '', label: svc.name ?? '' }))
                .filter((o) => o.id)}
              value={filterService}
              onChange={(value) => setListState({ service: value })}
              placeholder={t('tasks:filters.allServices')}
              noResultsText={t('tasks:filters.noResults')}
              className="w-full sm:w-52"
            />
          </div>
        </div>
      </div>

      {/* Content */}
      {error && initialLoadComplete ? (
        <Card>
          <EmptyState
            variant="error"
            title={t('common:errors.loadFailed')}
            description={error}
            onRetry={() => fetchTasks()}
          />
        </Card>
      ) : filteredTasks.length === 0 ? (
        <EmptyState
          icon={<Package className="w-12 h-12" strokeWidth={1.75} />}
          title={
            searchTerm || filterStatus !== 'all' || !!filterObject || !!filterService
              ? t('tasks:empty.titleFiltered')
              : t('tasks:empty.title')
          }
          description={
            searchTerm || filterStatus !== 'all' || !!filterObject || !!filterService
              ? t('tasks:empty.descriptionFiltered')
              : t('tasks:empty.description')
          }
          action={
            !searchTerm && filterStatus === 'all' && !filterObject && !filterService ? (
              <Button
                onClick={() => setIsCreateModalOpen(true)}
                leftIcon={<Plus className="w-4 h-4" strokeWidth={2} />}
              >
                {t('tasks:createTask')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <>
        <Card noPadding>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeader>{t('tasks:table.task')}</TableHeader>
                <TableHeader className="hidden md:table-cell">{t('tasks:table.object')}</TableHeader>
                <TableHeader className="hidden lg:table-cell">{t('tasks:table.service')}</TableHeader>
                <TableHeader className="hidden lg:table-cell">{t('tasks:table.duration')}</TableHeader>
                <TableHeader className="hidden sm:table-cell">{t('common:status.label')}</TableHeader>
                <TableHeader>{t('common:actions.title')}</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {pageItems.map((task) => (
                <TableRow
                  key={task.taskId}
                  onClick={() => navigate(`/tasks/${task.taskId}`)}
                >
                  <TableCell>
                    <div className="font-medium text-[13px] text-slate-900">
                      {task.name}
                    </div>
                    {/* Mobile: condensed info */}
                    <div className="text-[11px] text-slate-500 mt-0.5 space-y-0.5">
                      <div className="md:hidden">
                        {task.objectName}
                      </div>
                      <div className="lg:hidden flex items-center gap-1 tabular-nums">
                        <Clock className="w-3 h-3" strokeWidth={2} />
                        {formatDuration(task.estimatedDurationMinutes)} · {task.serviceName}
                      </div>
                      <div className="sm:hidden">
                        <Badge variant={task.isActive ? 'success' : 'default'}>
                          {task.isActive ? t('common:status.active') : t('common:status.inactive')}
                        </Badge>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <span className="text-[13px] text-slate-700">
                      {task.objectName}
                    </span>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <span className="text-[13px] text-slate-600">
                      {task.serviceName}
                    </span>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <div className="flex items-center gap-1 text-[13px] text-slate-600 tabular-nums">
                      <Clock className="w-3.5 h-3.5 text-slate-400" strokeWidth={2} />
                      {formatDuration(task.estimatedDurationMinutes)}
                    </div>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">
                    <Badge variant={task.isActive ? 'success' : 'default'}>
                      {task.isActive ? t('common:status.active') : t('common:status.inactive')}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 sm:gap-2" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/tasks/${task.taskId}`)}
                        title={t('tasks:table.actions.viewDetails')}
                        aria-label={t('tasks:table.actions.viewDetails')}
                        className="p-1.5"
                      >
                        <Eye className="w-4 h-4" strokeWidth={2} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(task)}
                        title={t('tasks:table.actions.edit')}
                        aria-label={t('tasks:table.actions.edit')}
                        className="p-1.5"
                      >
                        <Edit2 className="w-4 h-4" strokeWidth={2} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openDeleteDialog(task)}
                        title={t('tasks:table.actions.delete')}
                        aria-label={t('tasks:table.actions.delete')}
                        className="p-1.5"
                      >
                        <Trash2 className="w-4 h-4 text-red-600" strokeWidth={2} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
        <Pagination
          page={page}
          pageCount={pageCount}
          totalCount={filteredTasks.length}
          onPageChange={goToPage}
          disabled={loading}
        />
        </>
      )}

      {/* Create Modal */}
      <SlideOver
        isOpen={isCreateModalOpen}
        onClose={handleCloseModals}
        title={t('tasks:createTask')}
      >
        <TaskForm
          onClose={handleCloseModals}
          objects={objects}
          services={services}
        />
      </SlideOver>

      {/* Edit Modal */}
      <SlideOver
        isOpen={isEditModalOpen}
        onClose={handleCloseModals}
        title={t('tasks:editTask')}
      >
        {selectedTask && (
          <TaskForm
            task={selectedTask}
            onClose={handleCloseModals}
            objects={objects}
            services={services}
          />
        )}
      </SlideOver>

      {/* Delete Confirmation Dialog */}
      {/* Excel round-trip: two sheets — activities and their schedule rules */}
      <ExcelImportModal
        isOpen={isImportOpen}
        onClose={() => setIsImportOpen(false)}
        entity="tasks"
        title={t('tasks:actions.importTitle')}
        onImported={() => fetchTasks()}
      />

      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false);
          setTaskToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title={t('tasks:deleteTask')}
        message={t('tasks:confirmDelete', { name: taskToDelete?.name || '' })}
        confirmText={t('common:actions.delete')}
        cancelText={t('common:actions.cancel')}
        variant="danger"
      />
    </div>
  );
}
